import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../utils/api'
import { useDcStore } from '../stores/dcStore'
import { resolvePageDcCode } from '../utils/dcContext.logic'
import toast from 'react-hot-toast'
import { DndContext, useDraggable, useDroppable, DragEndEvent } from '@dnd-kit/core'
import { Server, Activity, Network, CheckCircle2 } from 'lucide-react'

// Draggable Device Card
function DraggableDevice({ device }: { device: any }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: device.id,
    data: device
  })

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 50,
  } : undefined

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-3.5 bg-bg-panel/60 backdrop-blur-md border rounded-xl mb-3 cursor-grab hover:border-accent hover:bg-bg-hover/20 transition-all duration-200 group relative overflow-hidden ${isDragging ? 'opacity-80 scale-105 shadow-xl shadow-accent/15 border-accent' : 'border-border/40 hover:shadow-lg'}`}
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-accent opacity-50 group-hover:opacity-100 transition-opacity" />
      <div className="flex items-start justify-between pl-2">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            {device.model?.toLowerCase().includes('switch') ? <Network className="w-4 h-4 text-accent" /> : <Server className="w-4 h-4 text-accent" />}
            <span className="font-bold text-text-primary text-sm tracking-wide">{device.name || 'Unknown Device'}</span>
          </div>
          <div className="font-mono text-[11px] text-text-muted">{device.ip_address} • {device.mac_address || 'No MAC'}</div>
          <div className="text-[10px] text-text-secondary uppercase tracking-wider">{device.manufacturer} {device.model}</div>
        </div>
        <div className="bg-status-warning/10 text-status-warning px-1.5 py-0.5 rounded text-[10px] font-semibold border border-status-warning/20 flex items-center gap-1 shrink-0">
          <Activity className="w-3 h-3" />
          STAGED
        </div>
      </div>
    </div>
  )
}

// Droppable Rack Visualization
function DroppableRackZone({ rackId, label }: { rackId: number; label: string }) {
  const { isOver, setNodeRef } = useDroppable({
    id: `rack-${rackId}`,
  })

  return (
    <div
      ref={setNodeRef}
      className={`relative rounded-2xl border-2 border-dashed transition-all duration-300 min-h-[350px] flex flex-col items-center justify-center p-8 bg-gradient-to-b from-bg-panel/20 to-bg-app
        ${isOver ? 'border-accent bg-accent/5 scale-[1.01]' : 'border-border/50'}`}
    >
      <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,#8080800d_1px,transparent_1px),linear-gradient(to_bottom,#8080800d_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      
      <div className={`p-4 rounded-full mb-4 transition-colors duration-300 ${isOver ? 'bg-accent/20 text-accent' : 'bg-bg-input text-text-muted'}`}>
        <Server className="w-8 h-8" />
      </div>
      <h3 className={`text-lg font-bold mb-2 transition-colors duration-300 ${isOver ? 'text-accent' : 'text-text-primary'}`}>
        {isOver ? `Поместить в стойку ${label}` : `Зона сброса: Стойка ${label}`}
      </h3>
      <p className="text-sm text-text-muted text-center max-w-[280px] leading-relaxed">
        Перетащите обнаруженное устройство сюда для автоматического размещения в стойке.
      </p>
    </div>
  )
}


export default function DiscoveryPage() {
  const { dcCode: urlDc } = useParams<{ dcCode?: string }>()
  const { activeDcCode } = useDcStore()
  const dcCode = resolvePageDcCode(urlDc, activeDcCode, undefined)
  const [loading, setLoading] = useState(false)
  const [devices, setDevices] = useState<any[]>([])
  const [racks, setRacks] = useState<any[]>([])
  const [selectedRackId, setSelectedRackId] = useState<number | null>(null)

  useEffect(() => {
    fetchDevices()
    if (dcCode) {
      api.get(`/api/datacenters/${dcCode}/racks`)
        .then(res => {
          setRacks(res.data)
          if (res.data.length > 0) {
            setSelectedRackId(res.data[0].id)
          } else {
            setSelectedRackId(null)
          }
        })
        .catch(() => {})
    }
  }, [dcCode])

  const fetchDevices = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/api/discovery/staging/${dcCode}`)
      setDevices(res.data)
    } catch (e) {
      toast.error("Failed to load staging devices")
    } finally {
      setLoading(false)
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    
    if (over && over.id.toString().startsWith('rack-')) {
      const deviceId = active.id as string
      const rackId = parseInt(over.id.toString().split('-')[1])
      
      const deviceObj = active.data.current;
      const isServer = deviceObj?.equipment_type === 'server' || deviceObj?.model?.toLowerCase().includes('r740');
      const uHeight = isServer ? 2 : 1;
      
      try {
        toast.loading('Размещение устройства...', { id: 'place' })
        
        await api.post(`/api/discovery/staging/${deviceId}/place`, {
          rack_id: rackId,
          u_position: 0,
          u_height: uHeight,
          mounting_position: 'Front'
        })
        
        setDevices(prev => prev.filter(d => d.id !== deviceId))
        toast.success(
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-status-normal" />
            <span>Устройство успешно размещено!</span>
          </div>,
          { id: 'place' }
        )
      } catch (e: any) {
        const errorMsg = e.response?.data?.detail || "Не удалось разместить устройство";
        toast.error(errorMsg, { id: 'place' })
      }
    }
  }

  const targetRack = racks.find(r => r.id === selectedRackId) || racks[0];
  const targetRackId = targetRack?.id || 1;
  const targetRackLabel = targetRack?.label || 'A-01';

  return (
    <div className="p-6 h-full flex flex-col animate-fade-in bg-bg-app">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Автообнаружение (Discovery)</h1>
          <p className="text-text-muted mt-1 text-sm">Сканирование подсетей и подтверждение найденного оборудования по протоколу SNMP.</p>
        </div>
        <button 
          onClick={fetchDevices} 
          disabled={loading}
          className="px-4 py-2.5 bg-accent hover:bg-accent-hover disabled:opacity-50 text-white text-xs font-semibold rounded-xl shadow-lg shadow-accent/15 transition-all flex items-center gap-2 hover:scale-[1.02]"
        >
          <Activity className="w-4 h-4" />
          Запустить сканирование
        </button>
      </div>
      
      <DndContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 flex-1 min-h-0">
          
          {/* Staging List (Left) */}
          <div className="lg:col-span-5 flex flex-col bg-bg-panel/40 border border-border/40 rounded-2xl overflow-hidden backdrop-blur-xl shadow-2xl">
            <div className="p-4 border-b border-border/40 bg-bg-panel/60">
              <h3 className="font-semibold text-sm tracking-wide text-text-primary flex items-center gap-2">
                <Network className="w-4 h-4 text-accent" />
                ОБНАРУЖЕННЫЕ УСТРОЙСТВА
                <span className="ml-auto bg-bg-input px-2 py-0.5 rounded-full text-xs font-bold text-text-secondary">{devices.length}</span>
              </h3>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-20 rounded-xl skeleton opacity-50" />
                  ))}
                </div>
              ) : devices.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-text-muted opacity-60">
                  <Activity className="w-12 h-12 mb-3 stroke-1" />
                  <p>Ожидание новых устройств...</p>
                </div>
              ) : (
                devices.map(dev => <DraggableDevice key={dev.id} device={dev} />)
              )}
            </div>
          </div>

          {/* Visualization (Right) */}
          <div className="lg:col-span-7 flex flex-col bg-bg-panel/40 border border-border/40 rounded-2xl overflow-hidden backdrop-blur-xl shadow-2xl">
            <div className="p-4 border-b border-border/40 bg-bg-panel/60 flex items-center justify-between">
              <h3 className="font-semibold text-sm tracking-wide text-text-primary flex items-center gap-2">
                <Server className="w-4 h-4 text-accent" />
                ЦЕЛЕВАЯ СТОЙКА (ВИЗУАЛИЗАЦИЯ)
              </h3>
              {racks.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-text-muted">Стойка:</span>
                  <select
                    value={selectedRackId || ''}
                    onChange={(e) => setSelectedRackId(Number(e.target.value))}
                    className="bg-bg-input border border-border/80 text-text-primary text-xs rounded-lg px-2.5 py-1 outline-none focus:border-accent cursor-pointer hover:bg-bg-hover transition-colors font-medium"
                  >
                    {racks.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.label} ({r.u_count}U)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="p-6 flex-1 flex flex-col justify-center">
               <DroppableRackZone rackId={targetRackId} label={targetRackLabel} />
            </div>
          </div>
          
        </div>
      </DndContext>
    </div>
  )
}
